<template>
  <div class="play-launch-checklist">
    <div class="row">
      <div class="col-12">
        <!-- Header Card -->
        <div class="card mb-4">
          <div class="card-header d-flex flex-wrap justify-content-between align-items-center">
            <h3 class="mb-0 mb-2 mb-sm-0" style="flex: 1 1 auto; min-width: 0;">Post-Launch Checklist</h3>
            <div class="deployment-checklist-button-wrapper d-flex justify-content-end">
              <button class="btn btn-secondary" @click="goBackToPlaybook">
                <img src="/img/icons/avid-icons/chevron-left.svg" class="icon" alt="">
                Back to Playbook
              </button>
            </div>
          </div>
          <div class="card-body">
            <p>Your play is being deployed to the selected destinations. You can monitor the progress below.</p>

            <!-- Loading state -->
            <div v-if="!deploymentData" class="text-center py-4">
              <div class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <span>Loading deployment data...</span>
            </div>

            <!-- Dynamic Destination Cards -->
            <div v-else class="row mt-4">
              <div v-for="destination in destinations" :key="destination.id" class="col-md-6 col-12 mb-4">
                <div class="content-card card h-100">
                  <div class="content-card-header card-header d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <!-- Image Icon -->
                      <img :src="destination.icon" v-if="destination.icon" class="icon me-2" style="width: 24px; height: 24px;">
                      <h4 class="mb-0">{{ destination.name }}</h4>
                    </div>
                    <!-- <span class="badge badge-primary" style="font-size: 14px;">{{ destination.assetCount }} touchpoint{{ destination.assetCount === 1 ? '' : 's' }}</span> -->
                  </div>
                  <div class="content-card-body card-body">
                    <div class="deployment-progress">
                      <div class="d-flex justify-content-between mb-2">
                        <p class="mb-0">Deploying to {{ deployment_channels[destination.exportId] || destination.name }}</p>
                        <span>{{ Math.round((deployment_progress[destination.exportId] || 0) * 100) }}%</span>
                      </div>
                      
                      <div v-if="deployment_messages[destination.exportId]" v-html="deployment_messages[destination.exportId]"></div>
                      <div v-else class="progress">

                          <div class="progress-bar bg-teal progress-bar-animated" 
                            role="progressbar" 
                            :style="{ width: `${(deployment_progress[destination.exportId] || 0) * 100}%` }" 
                            :aria-valuenow="(deployment_progress[destination.exportId] || 0) * 100" 
                            aria-valuemin="0" 
                            aria-valuemax="100">
                          </div>
                      </div>
                      <div v-if="deployment_progress[destination.exportId] === 1 && destination.items && destination.items.length" class="mt-3">
                        <p class="mb-2 small text-muted">Complete these steps to finish:</p>
                        <div class="form-check mb-1" v-for="(item, idx) in destination.items" :key="idx">
                          <input class="form-check-input" type="checkbox" :id="'check-' + destination.exportId + '-' + idx" :value="item.checked">
                          <label class="form-check-label small" :for="'check-' + destination.exportId + '-' + idx">{{ item.text }}</label>
                        </div>
                        <a v-if="deployment_urls[destination.exportId] && destination.name && destination.name.toLowerCase().includes('marketo')" :href="deployment_urls[destination.exportId]" target="_blank" rel="noopener noreferrer" class="btn btn-primary mt-2">Open playbook folder in Marketo</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useClientStore } from '../../stores/client';
import { usePlaybookStore } from '../../stores/playbook';

export default {
  name: 'DeploymentChecklist',
  props: {
    deploymentData: {
      type: Object,
      default: null
    },
    playbook: {
      type: Object,
      default: null
    },
    client: {
      type: Object,
      default: null
    }
  },
  setup() {
    const clientStore = useClientStore();
    const playbookStore = usePlaybookStore();
    return {
      clientStore,
      playbookStore
    };
  },
  data() {
    return {
      deploymentDataInternal: null,
      deployment_progress: {},
      deployment_channels: {},
      deployment_messages: {},
      deployment_urls: {},
      is_deploying: true,
      checklistItemsByExportId: {}
    };
  },
  computed: {
    playbookData() {
      return this.playbookStore.getSelectedPlaybook || window.__PLAYBOOK__ || this.playbook;
    },
    clientData() {
      return this.clientStore.getClient || window.__CLIENT__ || this.client;
    },
    deploymentDataComputed() {
      return this.deploymentDataInternal || window.__DEPLOYMENT_DATA__ || this.deploymentData;
    },
    destinations() {
      if (!this.deploymentDataComputed || !this.deploymentDataComputed.export_ids) {
        return [];
      }
      
      return Object.entries(this.deploymentDataComputed.export_ids).map(([, exportData]) => {
        const connectionName = exportData.connection_name;
        const connectionId = exportData.export_id.split('_')[0];
        
        return {
          id: connectionId,
          name: connectionName,
          icon: this.getConnectionIcon(connectionId, connectionName),
          items: this.checklistItemsByExportId[exportData.export_id] || [],
          exportId: exportData.export_id,
          assetCount: exportData.asset_ids.length
        };
      });
    },
    allDeploymentsComplete() {
      if (Object.keys(this.deployment_progress).length === 0) return false;
      return Object.keys(this.deployment_progress).every(
        exportId => this.deployment_messages[exportId] !== null && this.deployment_messages[exportId] !== undefined
      );
    }
  },
  watch: {
    deployment_progress: {
      handler(newVal) {
        // console.log('deployment_progress changed:', newVal);
        this.$forceUpdate();
      },
      deep: true
    },
    deployment_channels: {
      handler(newVal) {
        // console.log('deployment_channels changed:', newVal);
      },
      deep: true
    }
  },
  beforeMount() {
    // Initialize stores from window variables
    if (window.__CLIENT__) {
      this.clientStore.setClient(window.__CLIENT__);
    }
    if (window.__PLAYBOOK__) {
      this.playbookStore.initialize(window.__PLAYBOOK__);
    }
    if (window.__DEPLOYMENT_DATA__) {
      this.deploymentDataInternal = window.__DEPLOYMENT_DATA__;
    }
  },
  mounted() {
    // Set up Echo listeners for deployment progress
    // Data should be ready by the time this component mounts
    this.$nextTick(() => {
      if (this.destinations && this.destinations.length > 0) {
        this.fetchChecklistItems();
        this.setupDeploymentListeners();
      }
    });
  },
  beforeUnmount() {
    // Echo listeners will be automatically cleaned up when component unmounts
  },
  methods: {
    goBackToPlaybook() {
      // Navigate back to the playbook homepage
      window.location.href = `/playbooks/${this.clientData.url}/list`;
    },
    setupDeploymentListeners() {
      if (!this.destinations || this.destinations.length === 0) return;
      
      // console.log('Setting up deployment listeners for destinations:', this.destinations);
      
      // Clear existing properties instead of replacing objects
      Object.keys(this.deployment_progress).forEach(key => delete this.deployment_progress[key]);
      Object.keys(this.deployment_channels).forEach(key => delete this.deployment_channels[key]);
      Object.keys(this.deployment_messages).forEach(key => delete this.deployment_messages[key]);
      Object.keys(this.deployment_urls).forEach(key => delete this.deployment_urls[key]);
      let completedDeployments = new Set();
      
      // Set up listeners for each destination
      this.destinations.forEach(destination => {
        const exportId = destination.exportId;
        // console.log('Setting up listener for exportId:', exportId, 'channel:', `playbook.${this.clientData.id}.${exportId}`);
        this.deployment_progress[exportId] = 0;
        this.deployment_channels[exportId] = destination.name;
        // console.log('Set initial values:', {
        //   exportId,
        //   progress: this.deployment_progress[exportId],
        //   channel: this.deployment_channels[exportId]
        // });
        
        // Listen for progress updates
        window.Echo.private(`playbook.${this.clientData.id}.${exportId}`).listen('PlaybookExportUpdated', (e) => {
          // console.log('PlaybookExportUpdated received:', exportId, e.status, e.percentage);
          
          if (e.status === 'working') {
            this.deployment_progress[exportId] = e.percentage;
            this.deployment_messages[exportId] = null;
          } else if (e.status === 'completed') {
            this.deployment_progress[exportId] = 1;
            if (e.url) {
              this.deployment_urls[exportId] = e.url;
            }
            const isMarketo = destination.name && destination.name.toLowerCase().includes('marketo');
            this.deployment_messages[exportId] = '<div class="alert alert-success mb-0"><i class="fas fa-check-circle text-success me-2"></i><strong>Success!</strong> Your touchpoints have been deployed.</div>';
            completedDeployments.add(exportId);
          } else if (e.status === 'error') {
            this.deployment_progress[exportId] = 0;
            this.deployment_messages[exportId] = '<div class="alert alert-danger mb-0"><i class="fas fa-times-circle text-danger me-2"></i><strong>Error!</strong> There was an error deploying your playbook. ' + e.message + '</div>';
            completedDeployments.add(exportId);
          }
          
          // Check if all deployments are complete
          if (completedDeployments.size === this.destinations.length) {
            this.is_deploying = false;
          }
          
          this.$forceUpdate();
        });
      });
    },
    async fetchChecklistItems() {
      if (!this.deploymentDataComputed || !this.deploymentDataComputed.export_ids) {
        this.checklistItemsByExportId = {};
        return;
      }

      try {
        const response = await axios.post(`/api/playbooks/${this.clientData.url}/deployment_checklist_items`, {
          export_ids: this.deploymentDataComputed.export_ids
        });
        this.checklistItemsByExportId = response?.data?.items_by_export_id || {};
      } catch (error) {
        this.checklistItemsByExportId = {};
      }
    },
    getConnectionIcon(connectionId, connectionName) {
      // Map connection IDs and names to appropriate icons
      const iconMap = {
        'google_doc': '/img/icons/avid-icons/google-docs.svg',
        'Google Docs': '/img/icons/avid-icons/google-docs.svg',
        'facebook': '/img/icons/avid-icons/facebook.svg',
        'Facebook': '/img/icons/avid-icons/facebook.svg',
        'mailchimp': '/img/icons/avid-icons/mailchimp.svg',
        'MailChimp': '/img/icons/avid-icons/mailchimp.svg',
        'hubspot': '/img/icons/avid-icons/hubspot.svg',
        'HubSpot': '/img/icons/avid-icons/hubspot.svg',
        'marketo': '/img/icons/avid-icons/marketo.svg',
        'Marketo': '/img/icons/avid-icons/marketo.svg',
        'salesforce': '/img/icons/avid-icons/salesforce.svg',
        'Salesforce': '/img/icons/avid-icons/salesforce.svg',
      };
      
      // Try to match by connection ID first
      if (iconMap[connectionId]) {
        return iconMap[connectionId];
      }
      
      // Try to match by connection name
      if (iconMap[connectionName]) {
        return iconMap[connectionName];
      }
      
      // Fallback to name matching with normalized key
      const nameKey = connectionName.toLowerCase().replace(/\s+/g, '_');
      if (iconMap[nameKey]) {
        return iconMap[nameKey];
      }
      
      // Default icon
      return 'fas fa-circle';
    },
    isImageIcon(icon) {
      // Check if icon is an image path (starts with /)
      return icon && icon.startsWith('/');
    }
  }
};
</script>

<style scoped>
.deployment-checklist-button-wrapper {
  flex: 0 0 auto;
}

@media (max-width: 575.98px) {
  .deployment-checklist-button-wrapper {
    flex: 0 0 100%;
    width: 100%;
    margin-top: 0.5rem;
  }
}
</style>
